import { useCallback } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { FlowId, FlowStep, Flow, SubFlow } from './types';
import { useDefaultFlow } from './flows/useDefaultFlow';
import { useLightApplyFlow } from './flows/useLightApplyFlow';
import { useAutoApplySetupFlow } from './flows/useAutoApplySetupFlow';
import { useAutoApplyFlow } from './flows/useAutoApplyFlow';

// =============================================================================
// HELPER: Detect current flow from pathname
// =============================================================================

export const detectFlowId = (pathname: string): FlowId => {
    if (pathname.startsWith('/light-apply')) {
        return 'light-apply';
    } else if (pathname.startsWith('/auto-apply-setup')) {
        return 'auto-apply-setup';
    } else if (pathname.startsWith('/auto-apply')) {
        return 'auto-apply';
    } else if (pathname.startsWith('/default-apply')) {
        return 'default';
    } else {
        return 'default';
    }
};

// =============================================================================
// HOOK: useCurrentFlow
// =============================================================================

/**
 * Hook to get the current flow and its navigation utilities based on the current path
 * Auto-detects flow from URL:
 * - Paths starting with '/default-apply' → 'default' flow
 * - Paths starting with '/light-apply' → 'light-apply' flow
 * - Paths starting with '/auto-apply-setup' → 'auto-apply-setup' flow
 * - Paths starting with '/auto-apply' → 'auto-apply' flow
 * - All other paths → 'default' flow
 *
 * Each flow hook checks if it's active and returns a minimal stub if not,
 * avoiding unnecessary computation for inactive flows.
 *
 * @returns Object containing current flow data and navigation utilities
 */
export const useCurrentFlow = (): {
    /** Current flow ID */
    currentFlowId: FlowId;
    /** Current flow object */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentFlow: Flow<any>;
    /** All steps for current flow */
    allSteps: FlowStep[];
    /** Primary route of the flow's first step. Used by `usePageFlow` as the
     * cold-start return target / fallback when a sub-flow has no recorded origin. */
    firstStepRoute: string;
    /** Locate a route's 0-based index within whichever sub-flow contains it, or
     * `-1` if it isn't in any sub-flow. Uses `matchPath` so parameterized steps
     * (e.g. `/mid-setup-feedback/:category`) match the live URL. `usePageFlow`
     * uses this to decide whether the current route is a sub-flow step (index >= 0)
     * and how many entries `exitSubFlow` pops (`index + 1`) — purely from the route,
     * never from history state. */
    findSubFlowIndex: (route: string) => number;
    /** Return the sub-flow definition that owns a route (the detour the route is a
     * step of), or `null` if the route isn't in any sub-flow. `usePageFlow` uses it
     * on a cold/deep-link Back to step back within the detour and to resolve a
     * sub-flow's `defaultReturnRoute` when no in-app origin was recorded. */
    findSubFlow: (route: string) => SubFlow | null;
    /** Dynamic getNextStep function (all flows now implement this). Resolves the
     * next step purely from the current route: flows with sub-flows (auto-apply-setup)
     * detect sub-flow membership by route and advance within the owning sub-flow,
     * otherwise advance the main spine. No history/depth input is needed — the
     * route alone determines the step list. */
    getNextStep: (currentRoute: string) => Promise<string | null>;
    /** Loading state from current flow hook */
    loading: boolean;
} => {
    const location = useLocation();
    const currentFlowId = detectFlowId(location.pathname);

    // Each hook checks if it's active internally and returns stub if not
    // This avoids expensive operations (GraphQL queries, etc.) for inactive flows
    const defaultFlowData = useDefaultFlow(currentFlowId === 'default');
    const lightApplyFlowData = useLightApplyFlow(currentFlowId === 'light-apply');
    const autoApplySetupFlowData = useAutoApplySetupFlow(currentFlowId === 'auto-apply-setup');
    const autoApplyFlowData = useAutoApplyFlow(currentFlowId === 'auto-apply');

    // Get current flow data based on detected flow
    const currentFlowData = (() => {
        switch (currentFlowId) {
            case 'light-apply':
                return lightApplyFlowData;
            case 'auto-apply-setup':
                return autoApplySetupFlowData;
            case 'auto-apply':
                return autoApplyFlowData;
            case 'default':
            default:
                return defaultFlowData;
        }
    })();

    // Every flow hook returns subFlows (empty for flows without detours, e.g. all
    // but auto-apply-setup); consumed only here to build findSubFlowIndex.
    const { subFlows } = currentFlowData;
    const allSteps = currentFlowData.flow.steps;

    const findSubFlow = useCallback(
        (route: string): SubFlow | null =>
            subFlows.find((sub) =>
                sub.steps.some((step) =>
                    step.routes.some((stepRoute) => matchPath(stepRoute, route) !== null),
                ),
            ) ?? null,
        [subFlows],
    );

    const findSubFlowIndex = useCallback(
        (route: string): number =>
            findSubFlow(route)?.steps.findIndex((step) =>
                step.routes.some((stepRoute) => matchPath(stepRoute, route) !== null),
            ) ?? -1,
        [findSubFlow],
    );

    return {
        currentFlowId,
        currentFlow: currentFlowData.flow,
        allSteps,
        firstStepRoute: allSteps[0]?.routes[0] ?? '/',
        findSubFlowIndex,
        findSubFlow,
        getNextStep: currentFlowData.getNextStep,
        loading: currentFlowData.loading,
    };
};
